(function () {
  const SELECTOR = '[data-clinical-study-panel]';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const DEFAULT_TIMELINE_YEAR = '2024';

  if (window.__clinicalStudyPanelManager) {
    window.__clinicalStudyPanelManager.initAll(document);
    return;
  }

  class ClinicalStudyPanel {
    constructor(root) {
      this.root = root;
      this.revealElements = [];
      this.timelineButtons = [];
      this.timelineItems = [];
      this.revealObserver = null;
      this.motionQuery = typeof window.matchMedia === 'function' ? window.matchMedia(REDUCED_MOTION_QUERY) : null;
      this.reducedMotion = Boolean(this.motionQuery && this.motionQuery.matches);

      this.onTimelineClick = this.onTimelineClick.bind(this);
      this.onMotionChange = this.onMotionChange.bind(this);
    }

    init() {
      this.revealElements = Array.from(this.root.querySelectorAll('[data-clinical-reveal]'));
      this.timelineButtons = Array.from(this.root.querySelectorAll('[data-clinical-timeline-button]'));
      this.timelineItems = Array.from(this.root.querySelectorAll('[data-clinical-timeline-item]'));
      this.scrollRoot = this.root.closest('.rs-drawer__body');

      this.root.classList.add('is-enhanced');
      this.root.classList.toggle('clinical-study-panel--reduced-motion', this.reducedMotion);

      this.initTimeline();

      if (this.reducedMotion) {
        this.revealAll(false);
      } else if (typeof IntersectionObserver === 'function') {
        this.createRevealObserver();
      } else {
        this.revealAll(true);
      }

      if (this.motionQuery) {
        if (typeof this.motionQuery.addEventListener === 'function') {
          this.motionQuery.addEventListener('change', this.onMotionChange);
        } else {
          this.motionQuery.addListener(this.onMotionChange);
        }
      }

      return true;
    }

    destroy() {
      if (this.revealObserver) {
        this.revealObserver.disconnect();
        this.revealObserver = null;
      }

      this.timelineButtons.forEach((button) => {
        button.removeEventListener('click', this.onTimelineClick);
      });

      if (this.motionQuery) {
        if (typeof this.motionQuery.removeEventListener === 'function') {
          this.motionQuery.removeEventListener('change', this.onMotionChange);
        } else {
          this.motionQuery.removeListener(this.onMotionChange);
        }
      }
    }

    createRevealObserver() {
      this.revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.revealSection(entry.target, true);
            this.revealObserver?.unobserve(entry.target);
          });
        },
        {
          root: this.scrollRoot instanceof HTMLElement ? this.scrollRoot : null,
          rootMargin: '0px 0px -12% 0px',
          threshold: 0.2,
        }
      );

      this.revealElements.forEach((element) => {
        this.revealObserver.observe(element);
      });
    }

    revealAll(animateCounters) {
      this.revealElements.forEach((element) => {
        this.revealSection(element, animateCounters);
      });
    }

    revealSection(element, animateCounters) {
      if (!(element instanceof HTMLElement)) return;
      if (element.dataset.clinicalRevealed === 'true') return;

      element.dataset.clinicalRevealed = 'true';
      element.classList.add('is-visible');
      this.animateCountersIn(element, animateCounters);
    }

    animateCountersIn(container, shouldAnimate) {
      container.querySelectorAll('[data-clinical-counter]').forEach((counter) => {
        this.animateCounter(counter, shouldAnimate);
      });
    }

    animateCounter(counter, shouldAnimate) {
      if (!(counter instanceof HTMLElement)) return;
      if (counter.dataset.counterAnimated === 'true') return;

      const targetValue = Number.parseFloat(counter.dataset.counterValue || '');
      const prefix = counter.dataset.counterPrefix || '';
      const suffix = counter.dataset.counterSuffix || '';

      if (!Number.isFinite(targetValue)) return;

      counter.dataset.counterAnimated = 'true';

      if (!shouldAnimate || this.reducedMotion) {
        counter.textContent = `${prefix}${Math.round(targetValue)}${suffix}`;
        return;
      }

      const duration = 1600;
      const startTime = performance.now();

      const tick = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const currentValue = Math.round(targetValue * eased);

        counter.textContent = `${prefix}${currentValue}${suffix}`;

        if (progress < 1) {
          window.requestAnimationFrame(tick);
        }
      };

      window.requestAnimationFrame(tick);
    }

    initTimeline() {
      if (!this.timelineButtons.length || !this.timelineItems.length) return;

      this.timelineButtons.forEach((button) => {
        button.addEventListener('click', this.onTimelineClick);
      });

      const hasDefaultYear = this.timelineItems.some((item) => item.dataset.year === DEFAULT_TIMELINE_YEAR);
      this.setActiveTimeline(hasDefaultYear ? DEFAULT_TIMELINE_YEAR : this.timelineItems[0]?.dataset.year);
    }

    onTimelineClick(event) {
      const button = event.currentTarget;
      if (!(button instanceof HTMLElement)) return;

      this.setActiveTimeline(button.dataset.year);
    }

    setActiveTimeline(year) {
      if (!year) return;

      this.timelineButtons.forEach((button) => {
        const isActive = button.dataset.year === year;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        button.tabIndex = isActive ? 0 : -1;
      });

      this.timelineItems.forEach((item) => {
        const isActive = item.dataset.year === year;
        item.classList.toggle('is-active', isActive);
        item.hidden = !isActive;
      });
    }

    onMotionChange(event) {
      this.reducedMotion = Boolean(event.matches);
      this.root.classList.toggle('clinical-study-panel--reduced-motion', this.reducedMotion);

      if (this.reducedMotion) {
        if (this.revealObserver) {
          this.revealObserver.disconnect();
          this.revealObserver = null;
        }

        this.revealAll(false);
      }
    }
  }

  const manager = {
    instances: new Map(),

    initAll(container) {
      const scope = container instanceof Element || container instanceof Document ? container : document;
      scope.querySelectorAll(SELECTOR).forEach((root) => {
        this.initRoot(root);
      });
    },

    initRoot(root) {
      if (!(root instanceof HTMLElement)) return;
      if (this.instances.has(root)) return;

      const instance = new ClinicalStudyPanel(root);
      if (!instance.init()) return;

      this.instances.set(root, instance);
    },

    destroyIn(container) {
      const scope = container instanceof Element || container instanceof Document ? container : document;
      scope.querySelectorAll(SELECTOR).forEach((root) => {
        this.destroyRoot(root);
      });
    },

    destroyRoot(root) {
      if (!(root instanceof HTMLElement)) return;

      const instance = this.instances.get(root);
      if (!instance) return;

      instance.destroy();
      this.instances.delete(root);
    },
  };

  const onReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
      return;
    }

    callback();
  };

  onReady(() => {
    manager.initAll(document);
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof HTMLElement) {
      manager.initAll(event.target);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target instanceof HTMLElement) {
      manager.destroyIn(event.target);
    }
  });

  window.__clinicalStudyPanelManager = manager;
})();
